import React, { useEffect, SubmitEvent } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { ILoginData } from "../../types/authTypes";
import { useAlertHelpers } from "../../helper/Hooks/useAlertHelpers";
import { useSession } from "../../contexts/SessionContext";
import { useNavigate } from "react-router";
import { useLoginState } from "../login/useLoginState";
import { adminLoginConfig } from "../login/loginConfigs";
import SharedLogin from "../login/sharedLogin";

interface LoginClinicAdminProps {
  title: string;
}

/**
 * function component of logining clinic admin
 * - use `useLoginState` hook to manage state
 * - use `SharedLogin` component to render UI
 * - include only admin-sepecific business logic
 */
const LoginClinicAdmin = ({ title }: LoginClinicAdminProps) => {
  const navigate = useNavigate();
  const { showAlertModal, reportNoClinicSelected } = useAlertHelpers();

  const authContext = useAuth();
  const { login: authLogin } = authContext;
  const { login: sessionLogin } = useSession();

  const loginState = useLoginState(adminLoginConfig);

  /**Set the page title*/
  useEffect(() => {
    document.title = title;
  }, [title]);

  /**
   * Method for handle the login.
   * The input data are stringified to JSON.
   * Then send JSON to backend API and handle the response.
   */
  const handleLogin = async (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!loginState.username) {
      console.warn("Kein Username");
      showAlertModal("Kein Nutzername angegeben.");
      return;
    }

    if (!loginState.password) {
      console.warn("Kein Passwort");
      showAlertModal("Kein Passwort angegeben.");
      return;
    }

    if (!loginState.clinicId || loginState.clinicId === "") {
      console.warn("Keine Klinik");
      reportNoClinicSelected();
      return;
    }

    // construct login data
    const loginData: ILoginData = {
      clinicName: loginState.clinicname,
      clinicId: loginState.clinicId,
      username: loginState.username,
      password: loginState.password,
      role: "clinic-admin",
      userType: "admin",
    };

    if (typeof authLogin === "function") {
      await authLogin(loginData, (authResponse: string) => {
        if (authResponse === "success") {
          sessionLogin();
          navigate("/admin");
        } else if (authResponse === "credentials_wrong") {
          showAlertModal(
            "Ihre Anmeldedaten sind fehlerhaft oder Ihr Account wurde noch nicht freigeschaltet.",
          );
        } else if (authResponse === "no_permissions") {
          showAlertModal("Sie haben für diese Seite keine Berechtigung.");
        } else {
          showAlertModal(
            "Irgendetwas ist schief gelaufen. Bitte versuchen Sie es erneut.",
          );
        }
      });
    }
  };

  return (
    <SharedLogin
      config={adminLoginConfig}
      clinics={loginState.clinics}
      onClinicIdChange={loginState.handleClinicId}
      onUsernameChange={loginState.handleUsername}
      onPasswordChange={loginState.handlePassword}
      onSubmit={handleLogin}
    />
  );
};

export default LoginClinicAdmin;
